import React, { createContext, useContext, useMemo } from 'react';
import { useI18n } from '../i18n.js';
import { localizeBotNames } from './botDisplayNames.js';
import { useGameSocket, type ClientState, type GameActions } from './useGameSocket.js';
import { useVoiceChat, type VoiceChatController } from './useVoiceChat.js';

interface GameContextValue {
  state: ClientState;
  actions: GameActions;
  voice: VoiceChatController;
}

export const GameContext = createContext<GameContextValue | null>(null);

export function GameProvider({ children }: { children: React.ReactNode }) {
  const { state, actions } = useGameSocket();
  const { lang } = useI18n();
  const displayState = useMemo(() => localizeBotNames(state, lang), [state, lang]);
  const voice = useVoiceChat({
    roomId: state.roomId,
    playerId: state.playerId,
    voiceEnabled: state.voiceEnabled,
    participants: state.voiceParticipants,
    isVip: state.profile?.tier === 'paid',
    setVoiceState: actions.setVoiceState,
    sendVoiceSignal: actions.sendVoiceSignal,
    subscribeVoiceSignal: actions.subscribeVoiceSignal,
  });
  return <GameContext.Provider value={{ state: displayState, actions, voice }}>{children}</GameContext.Provider>;
}

export function useGameState() {
  const ctx = useContext(GameContext);
  if (!ctx) throw new Error('useGameState must be used within GameProvider');
  return ctx;
}
